import { Add, FileUploadRounded, TuneRounded } from '@mui/icons-material';
import { Badge, Box, Container, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import CollectionAccordion from '../../components/DealGPT/PhaseTwo/QuestionLibrary/CollectionAccordion';
import CollectionAccordionSkeleton from '../../components/DealGPT/PhaseTwo/QuestionLibrary/CollectionAccordionSkeleton';
import CollectionForm from '../../components/DealGPT/PhaseTwo/QuestionLibrary/CollectionForm';
import ImportCollectionDialog from '../../components/DealGPT/PhaseTwo/QuestionLibrary/ImportCollectionDialog';
import QuestionLibraryFilters from '../../components/DealGPT/PhaseTwo/QuestionLibrary/QuestionLibraryFilters';
import { AnalyticsEvent, useAnalytics } from '../../components/Providers/AnalyticsProvider';
import { useAuth } from '../../components/Providers/AuthProvider';
import { useFeatures } from '../../components/Providers/FeatureProvider';
import { useFetchData } from '../../hooks/useFetchData';
import { createCollection } from '../../services/dealGPT';
import { CollectionWithCreator } from '../../Types/dealGPT';
import { collectionsPathUrl } from '../../util/routesUrls';

export enum QuestionLibraryTabs {
  MY_COLLECTIONS = 0,
  SHARED_COLLECTIONS = 1,
  PUBLIC_COLLECTIONS = 2,
}

const QuestionLibrary: React.FC = () => {
  const { currentUser } = useAuth();
  const analytics = useAnalytics();
  const features = useFeatures();

  const [tab, setTab] = useState<QuestionLibraryTabs>(() => {
    const stored = currentUser && localStorage.getItem('questionLibraryTab');
    return stored !== null ? Number(stored) : QuestionLibraryTabs.MY_COLLECTIONS;
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [showImport, setShowImport] = useState(false);
  const [newlyCreatedId, setNewlyCreatedId] = useState<string | null>(null);

  const {
    data: collections,
    loading: loadingCollections,
    refetch: refetchCollections,
  } = useFetchData<CollectionWithCreator[]>({
    urlPath: collectionsPathUrl({
      owners:
        currentUser && tab === QuestionLibraryTabs.MY_COLLECTIONS
          ? [currentUser.id]
          : selectedUsers,
      visibility: tab === QuestionLibraryTabs.PUBLIC_COLLECTIONS ? ['public'] : [],
      tags: selectedTags,
      shared: tab === QuestionLibraryTabs.SHARED_COLLECTIONS,
    }),
    skip: !currentUser,
  });

  const handleCreateCollection = useCallback(
    async (name: string, description?: string, tags?: string[]) => {
      try {
        analytics.event(
          AnalyticsEvent.WM_IA_QL_ADD_COLLECTION,
          {
            value: `Collection=${name},Tags=${tags?.join(',')},Visibility=private`,
          },
          {
            collectionName: name,
            collectionTags: tags,
            collectionVisibility: 'private',
          }
        );

        const newCollection = await createCollection({
          name,
          description,
          tags,
          visibility: 'private',
        });
        refetchCollections();
        setIsCreating(false);
        setNewlyCreatedId(newCollection.id);
      } catch {
        enqueueSnackbar('Failed to create collection', { variant: 'error' });
      }
    },
    [refetchCollections, analytics]
  );

  useEffect(() => {
    if (currentUser && tab === QuestionLibraryTabs.MY_COLLECTIONS) {
      setSelectedUsers([currentUser.id]);
    }
    if (
      tab === QuestionLibraryTabs.PUBLIC_COLLECTIONS ||
      tab === QuestionLibraryTabs.SHARED_COLLECTIONS
    ) {
      setSelectedUsers([]);
      setIsCreating(false);
    }
  }, [tab, currentUser]);

  useEffect(() => {
    localStorage.setItem('questionLibraryTab', tab.toString());
  }, [tab]);

  const filtersTooltip = useMemo(() => {
    if (selectedUsers.length === 0 && selectedTags.length === 0) return 'Filters';

    const usersPart =
      selectedUsers.length > 0
        ? `${selectedUsers.length} User${selectedUsers.length > 1 ? 's' : ''}`
        : '';

    const tagsPart =
      selectedTags.length > 0
        ? `${selectedUsers.length > 0 ? ' and ' : ''}${selectedTags.length} Tag${
            selectedTags.length > 1 ? 's' : ''
          }`
        : '';

    return `Filtering by ${usersPart}${tagsPart}`;
  }, [selectedUsers, selectedTags]);

  return (
    <>
      <div
        style={{
          overflow: 'auto',
          width: '100%',
          height: '100%',
          maxHeight: 'calc(100vh - var(--banner-height, 0px))',
        }}
      >
        <Container maxWidth="lg" sx={{ paddingBottom: 10 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              paddingTop: 3,
              gap: 1,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: '700' }}>
              Question Library
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
                <Tab label="My Questions" value={QuestionLibraryTabs.MY_COLLECTIONS} />
                <Tab label="Shared with me" value={QuestionLibraryTabs.SHARED_COLLECTIONS} />
                <Tab label="Public Questions" value={QuestionLibraryTabs.PUBLIC_COLLECTIONS} />
              </Tabs>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {tab === QuestionLibraryTabs.MY_COLLECTIONS && (
                  <Tooltip title="New Collection" arrow>
                    <IconButton onClick={() => setIsCreating(true)}>
                      <Add />
                    </IconButton>
                  </Tooltip>
                )}
                {tab === QuestionLibraryTabs.MY_COLLECTIONS && features.ql_import_collection && (
                  <Tooltip title="Import Collection" arrow>
                    <IconButton onClick={() => setShowImport(true)}>
                      <FileUploadRounded />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={filtersTooltip} arrow>
                  <Badge
                    badgeContent={selectedUsers.length + selectedTags.length}
                    color="primary"
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    overlap="circular"
                  >
                    <IconButton onClick={() => setShowFilters(true)}>
                      <TuneRounded />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                padding: 1,
              }}
            >
              {isCreating && (
                <CollectionForm
                  onSave={handleCreateCollection}
                  onCancel={() => setIsCreating(false)}
                />
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {loadingCollections ? (
                  Array.from({ length: 8 }, (_, index) => (
                    <CollectionAccordionSkeleton
                      key={index}
                      hasActions={tab !== QuestionLibraryTabs.PUBLIC_COLLECTIONS}
                    />
                  ))
                ) : collections && collections.length > 0 ? (
                  collections.map((collection) => (
                    <CollectionAccordion
                      tab={tab}
                      key={collection.id}
                      collection={collection}
                      defaultExpanded={collections.length === 1 || collection.id === newlyCreatedId}
                      autoScroll={collection.id === newlyCreatedId}
                      onScrolled={() => setNewlyCreatedId(null)}
                      refetchCollections={refetchCollections}
                    />
                  ))
                ) : (
                  <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                    No collections found. Create a new collection to get started.
                  </Typography>
                )}
              </Box>
            </Box>
            <QuestionLibraryFilters
              open={showFilters}
              tab={tab}
              selectedTags={selectedTags}
              selectedUsers={selectedUsers}
              onClear={() => {
                if (tab !== QuestionLibraryTabs.MY_COLLECTIONS) setSelectedUsers([]);
                setSelectedTags([]);
                setShowFilters(false);
              }}
              onClose={() => setShowFilters(false)}
              onApply={(users, tags) => {
                setSelectedUsers(users);
                setSelectedTags(tags);
                setShowFilters(false);
              }}
            />
          </Box>
        </Container>
      </div>
      {showImport && (
        <ImportCollectionDialog
          onImportCompleted={(newCollectionId: string) => {
            refetchCollections();
            setNewlyCreatedId(newCollectionId);
          }}
          onClose={() => setShowImport(false)}
        />
      )}
    </>
  );
};

export default QuestionLibrary;
